<template>
  <article
    class="staff-grid4-item"
    :class="{ 'staff-grid4-item--clickable': openPhotoOnClick }"
    :role="openPhotoOnClick ? 'button' : null"
    :tabindex="openPhotoOnClick ? 0 : null"
    @click="onCardClick"
    @keydown.enter.prevent="onCardClick"
    @keydown.space.prevent="onCardClick"
  >
    <div class="staff-grid4-item__photo">
      <img
        :src="people_item.imageThumb || people_item.image"
        :alt="people_item.name"
        loading="lazy"
        decoding="async"
        width="256"
        height="314"
      >
    </div>
    <div class="staff-grid4-item__body">
      <p class="staff-grid4-item__name">
        <span v-if="surname" class="staff-grid4-item__surname">{{ surname }}</span>
        <span v-if="firstName" class="staff-grid4-item__first-name">{{ firstName }}</span>
        <span v-if="patronymic" class="staff-grid4-item__patronymic">{{ patronymic }}</span>
      </p>
      <p v-if="positionLine" class="staff-grid4-item__position">{{ positionLine }}</p>
      <p v-if="departmentLine" class="staff-grid4-item__department">{{ departmentLine }}</p>
      <router-link
        v-if="showDetailLink && detailLink"
        :to="detailLink"
        class="staff-grid4-item__detail"
        @click.stop
      >
        Подробнее
        <img src="../../assets/svg/arrow-right.svg" alt="" class="staff-grid4-item__detail-arrow">
      </router-link>
    </div>
  </article>
</template>

<script>
export default {
  name: "StaffGrid4PeopleItem",
  props: {
    people_item: {
      type: Object,
      required: true,
    },
    /** По умолчанию true — поведение блока конструктора; на каталоге можно отключить */
    showQualificationCategory: {
      type: Boolean,
      default: true,
    },
    /** По умолчанию false — отделение только где явно включено (каталог) */
    showDepartment: {
      type: Boolean,
      default: false,
    },
    /** Клик по всей карточке открывает модалку — только где явно включено */
    openPhotoOnClick: {
      type: Boolean,
      default: false,
    },
    showDetailLink: {
      type: Boolean,
      default: false,
    },
    detailLink: {
      type: String,
      default: '',
    },
  },
  computed: {
    nameParts() {
      const parts = String(this.people_item.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      return {
        surname: parts[0] || "",
        firstName: parts[1] || "",
        patronymic: parts.slice(2).join(" "),
      };
    },
    surname() {
      return this.nameParts.surname;
    },
    firstName() {
      return this.nameParts.firstName;
    },
    patronymic() {
      return this.nameParts.patronymic;
    },
    positionLine() {
      const position = String(this.people_item.position || "").trim();
      if (!this.showQualificationCategory) {
        return position;
      }
      const category =
        this.people_item.qualificationCategory ||
        this.people_item.qualification_category ||
        this.people_item.category ||
        "";
      const cat = String(category).trim();
      if (position && cat) {
        return `${position}, ${cat}`;
      }
      return position || cat || "";
    },
    departmentLine() {
      if (!this.showDepartment) {
        return "";
      }
      const main = String(this.people_item.mainDepartment || "").trim();
      const dept = String(this.people_item.department || "").trim();
      const sub = String(this.people_item.subDepartment || "").trim();
      return sub || dept || main || "";
    },
  },
  methods: {
    onCardClick() {
      if (!this.openPhotoOnClick || !this.people_item) {
        return;
      }
      this.$emit("open-photo", this.people_item);
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;
$text-muted: #6b7280;
$photo-bg: #f4f4f4;

.staff-grid4-item {
  display: flex;
  align-items: stretch;
  gap: 14px;
  min-width: 0;
  height: 100%;
  padding: 14px;
  background: #fff;
  border: 1px solid #e8ece8;
  border-radius: 14px;
  box-sizing: border-box;

  &--clickable {
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: #c5d4c6;
      box-shadow: 0 2px 8px rgba(102, 204, 102, 0.1);
      outline: none;
    }
  }
}

.staff-grid4-item__photo {
  flex: 0 0 96px;
  width: 96px;
  height: 112px;
  border-radius: 12px;
  overflow: hidden;
  background: $photo-bg;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }
}

.staff-grid4-item__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.staff-grid4-item__name {
  display: flex;
  flex-direction: column;
  margin: 0 0 6px;
  font-family: "TTDrugs", sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
  color: $green-text;
}

.staff-grid4-item__surname,
.staff-grid4-item__first-name,
.staff-grid4-item__patronymic {
  display: block;
}

.staff-grid4-item__position {
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 13px;
  line-height: 1.35;
  color: $text-muted;
}

.staff-grid4-item__department {
  margin: 4px 0 0;
  font-family: "Circe", sans-serif;
  font-size: 12px;
  line-height: 1.35;
  color: $text-muted;
}

.staff-grid4-item__detail {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: auto;
  padding-top: 8px;
  font-family: "Circe", sans-serif;
  font-size: 13px;
  color: $green-text;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.staff-grid4-item__detail-arrow {
  width: 12px;
  height: 12px;
}
</style>
